import Auth from '../Auth/Auth';
import ls from '../lib/localStorage';
import pWaitFor from 'p-wait-for';
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import {socket} from "../lib/picos/socket";
import {globalAbortSignal} from "../lib/picos/abortController";

export enum PollingOptions {
    FULL = 'full',
    PARTIAL = 'partial'
}

export type PollingOptionTypes = 'full' | 'partial';

/**
 * Should we do excessive logging?
 */
let logging = ls.getBoolean(ls.QUEUE_LOGGING, false);

/**
 * This is the timeout that we set after each run.
 * It is cleared when we stop polling, and set when we start again.
 */
// let refreshInterval: NodeJS.Timeout;

/**
 * Timeout called every time something is saved to the queue. It is a 1 second timeout, which gives some time for
 * other changes to be added. This timeout will be canceled and reset every time more data is added to the queue.
 */
// let checkQueueTimeout: NodeJS.Timeout;

/**
 * Is the exec function currently running?
 *
 * @type {boolean}
 */
let isExecRunning: boolean = false;

/**
 * Is the start method running?
 *
 * @type {boolean}
 */
let inStart: boolean = false;

/**
 * Is pooling enabled?
 *
 * @type {boolean}
 */
let pollingEnabled: boolean = false;

/**
 * Array of callbacks to call when the scheduler runs.  Order of execution is not guaranteed.
 * @type {Array<Function>}
 */
let callbacks: Function[] = [];

const pollingFormat = "background: #FFD580; color: black;padding: 4px; border-radius: 3px;";
const pollingErrorFormat = "background: #FFD580; color: maroon;padding: 4px; border-radius: 3px;";

/**
 * Based on the last partial, are we doing a full or partial refresh?
 *
 * @returns {PollingOptionTypes}
 */
export function getExecType(): PollingOptionTypes {
    return ls.getString(ls.REFRESH_TYPE, 'full', true) as PollingOptionTypes;
}

/**
 * This function is called after a change is made to data that needs to be sent to the server.
 * We set a 1-second timeout, so if more data is changed in that time we reset the timer, essentially
 * waiting for one large group of data to send over.
 *
 * @param {function} [cb] - function to call when finished with queue processing (not currently used)
 */
// export function checkQueue(cb?: Function) {
//     // If the timer is set, reset it
//     if (checkQueueTimeout) {
//         if (logging) console.debug('%c⮃ checkQueue clearTimeout', pollingFormat);
//         clearTimeout(checkQueueTimeout);
//     }
//
//     if (logging) console.debug('%c⮃ checkQueue setTimeout', pollingFormat);
//     if (!!cb) checkQueueTimeout = setTimeout(exec, 1000, 'partial', 'checkQueue', cb);
// }

/**
 * Start polling.  Usually called when we get a connection to the server.
 *
 * @param {string} from
 */
export async function start(from: string) {
    if (pollingEnabled) {
        if (logging) console.debug(`%c⮃ Polling: start called from ${from}, but already polling`, pollingFormat);
        return;
    }
    else pollingEnabled = true;

    if (logging) console.debug(`%c⮃ Polling: start called from ${from}`, pollingFormat);

    // Mutex: if we are already in this method, return
    if (inStart) {
        if (logging) console.debug('%c⮃ Polling: Already in start', pollingFormat);
        return;
    }

    // Mutex-like functionality
    inStart = true;

    try {
        // Run immediately first, then wait
        if (logging) console.debug('%c⮃ Polling: initial call to exec after polling start', pollingFormat);

        let execFailed = false;

        if (!isExecRunning) await exec(getExecType(), `${from} => start`, (status: any) => {
            if (status) {
                execFailed = true;
                if (logging) console.debug(`%c⮃ Polling: Returned from exec with error ${status}`, pollingErrorFormat);
            }
            else if (logging) console.debug('%c⮃ Polling: Returned from exec successfully', pollingFormat);
        });

        // If exec failed (e.g. socket not connected), reset pollingEnabled so a future
        // call to start() (e.g. from socketUp on reconnect) can retry the data fetch.
        if (execFailed) {
            pollingEnabled = false;
        }
    }
    finally {
        // Always ensure that we reset the start flag! If not, reconnection will not restart the queue
        inStart = false;
    }
}

/**
 * Stop polling.  Usually called when we lose the connection to the server or log out.
 *
 * Clears the timeout and sets run to false.
 *
 * @param {string} from
 */
export function stop(from: string) {
    if (pollingEnabled) {
        pollingEnabled = false;

        if (logging) console.debug(`%c⮃ Polling: stopped called from ${from}`, pollingFormat);
    }
    else {
        if (logging) console.debug(`%c⮃ Polling: stopped called from ${from}, but already stopped`, pollingFormat);
    }
}

/**
 * Call full refresh externally (Support, change of firm, etc)
 * @param {string} requesterName - who is requesting?
 * @param {function} [callback] - function to call when finished
 */
export function doFullRefresh(requesterName: string, callback?: Function) {
    // Set the last partial time to 0, forcing a full refresh
    ls.setString(ls.REFRESH_TYPE, 'full', true);

    if (logging) console.debug('%c⮃ Polling: doFullRefresh', pollingFormat, pollingFormat, requesterName);

    const doExec = () => exec('full', `${requesterName} => doFullRefresh`, (status: string) => {
        if (logging) {
            if (status) console.debug(`%c⮃ Returned from exec with error ${status}`, pollingErrorFormat);
            else console.debug('%c⮃ Polling: Returned from exec successfully', pollingFormat);
        }

        if (typeof (callback) === 'function') callback();
    }).catch(err => console.error(`%c⮃ Polling: error running exec(): ${err?.message}`, pollingErrorFormat, err));

    // If exec is not running, we run it now, forcing the full refresh
    if (!isExecRunning) doExec().catch(err => console.error('%c⮃ Polling', pollingErrorFormat, err));
    else pWaitFor(() => !isExecRunning, {interval: 1000, signal: globalAbortSignal})
        .then(() => doExec())
        .catch(err => console.error('%c⮃ Polling', pollingErrorFormat, err));
}

/**
 * Call full refresh externally (Support, change of firm, etc)
 * @param {string} requesterName - who is requesting?
 * @param {Function} [callback] - function to call when finished
 */
export function doPartialRefresh(requesterName: string, callback?: Function) {
    if (logging) console.debug('%c⮃ Polling: doPartialRefresh', pollingFormat, requesterName, ls.getString(ls.REFRESH_TYPE, 'full', true));

    // If exec is not running, we run it now, forcing the partial refresh
    if (!isExecRunning) {
        exec('partial', `${requesterName} => doPartialRefresh`, (status: any) => {
            if (logging) {
                if (status) console.debug(`%c⮃ Polling: Returned from exec with error ${status}`, pollingErrorFormat);
                else console.debug('%c⮃ Returned from exec successfully', pollingFormat);
            }

            if (typeof (callback) === 'function') callback();
        }).catch(err => console.error(`%c⮃ Polling: error running exec(): ${err?.message}`, pollingErrorFormat, err));
    }
    else {
        console.debug('%c⮃ Polling: doPartialRefresh called while exec running', pollingFormat);
    }
}

/**
 * Add a callback to the scheduler
 * @param {string} from
 * @param {Function} callback
 */
export function addCallback(from: string, callback: Function) {
    if (logging) console.debug(`⮃ Polling: addCallback called from ${from}`, pollingFormat);

    if (callbacks.indexOf(callback) === -1)
        callbacks.push(callback);

    if (logging) console.debug(`%c⮃ Polling: Added polling listener, listener count is now ${callbacks.length}`, pollingFormat);
}

/**
 * Remove a callback from the scheduler
 * @param {Function} callback
 */
export function removeCallback(callback: Function) {
    callbacks = callbacks.filter(cb => callback !== cb);
}

/**
 * Complete rewrite of this functionality.  We used to use algorithms to figure out when to call processQueue, and
 * when to do a full refresh.  We have greatly simplified this now:
 * - processQueue is now called from redux when something has changed, indicating that we need to send data to the server
 * - full refreshes have been replaced with scheduled partial refreshes, which are called by an interval handler.
 * - a full refresh only happens when outlaw.fetch.lastFetch is set to 0.
 * - only one callback is ever called: processQueue.
 *
 * Call each of the callbacks in the callback array, one at a time.  The only one right now is from App: processQueue.
 * Pass an options object that contains the fullRefresh flag.
 *
 * @param {PollingOptionTypes} type - what kind of execution are we doing, full or partial?
 * @param {string} from - who called us?
 * @param {Function} [callback] - function to call when work is done
 */
async function exec(type: PollingOptionTypes, from: string, callback: Function) {
    if (logging) console.debug('%c⮃ Polling: exec', pollingFormat, from);
    if (logging) if (isExecRunning) console.debug(`%c⮃ exec() called while already running`, pollingFormat);

    isExecRunning = true;

    try {
        // Ensure the socket is connected. Wait 5 seconds for connection before bailing.
        if (!socket?.connected) {
            try {
                await pWaitFor(() => !!socket?.connected, {timeout: 5_000, signal: globalAbortSignal});
            }
            catch (err) {
                if (logging) console.warn('%c⮃ Polling: exec called when not connected', pollingFormat);
                if (typeof (callback) === 'function') callback('exec called when not connected', pollingFormat);
                return;
            }
        }

        // Ensure the user is authenticated. Wait 5 seconds before bailing.
        if (!Auth.isAuthenticated() && getCurrentFirmId() && getCurrentUserId()) {
            try {
                if (logging) console.debug('%c⮃ Polling: waiting for user to authenticate', pollingFormat);

                await pWaitFor(() => Auth.isAuthenticated() && !!getCurrentFirmId() && !!getCurrentUserId(),
                    {timeout: 5_000, interval: 200, signal: globalAbortSignal}
                );

                if (logging) console.debug('%c⮃ Polling: user authenticated', pollingFormat);
            }
            catch (err) {
                if (logging) console.warn('%c⮃ Polling: exec waiting 5 seconds for the user to authenticate, moving on', pollingFormat);
                return;
            }
        }

        // Call the callback with an options object
        const options = {type};
        const promises = [];
        const results = [];

        // Each call results in a promise, which we add to the results array.
        // We then wait for all of those Promises to resolve before continuing.
        // Right now the only callback is processQueue, which is called indirectly
        // from polling.js (through this method), but is also called indirectly
        // by redux sagas.
        for (let i = 0; i < callbacks.length; i++) {
            if (logging) console.debug(`⮃ Polling: exec calling callback #${i + 1}`, pollingFormat);
            promises[i] = callbacks[i](options).then((res: any) => results.push(res));
        }

        await Promise.allSettled(promises);

        // See if there are any new lookup registry entries to create
        if (logging) console.debug('%c⮃ Polling: Check for new lookup registry entries', pollingFormat);

        if (typeof (callback) === 'function') {
            if (logging) console.debug('%c⮃ Polling: exec called all callbacks successfully', pollingFormat);

            callback();
        }
    }
    catch (err: any) {
        console.error(`%c⮃ Polling`, pollingErrorFormat, err);

        if (callback) callback(err.message);
    }
    finally {
        if (logging) console.debug('%c⮃ Polling: exec finished', pollingFormat);

        isExecRunning = false;
    }
}
